import React, { FunctionComponent, useEffect, useState } from 'react';
import { compose } from 'recompose';
import withStyles from 'react-jss';
import { withRouter, match } from 'react-router-dom';
import {
  Grid,
  Container,
  Header,
  GridRow,
  GridColumn,
} from 'semantic-ui-react';
import { pathOr, isEmpty } from 'ramda';

import Query from 'components/Query';
import { ChartPlaceholder } from 'components/Placeholder';
import { usePlacesSearch } from 'helpers/usePlacesSearch';
import { ClassProp } from 'helpers/types';
import PlacesMap from 'components/PlacesMap';

import VenuesList from './sections/VenuesList';
import { GET_PLACES } from './Tier3VenueOnboarding.queries';
import {
  GeoViewPort,
  Place,
  PlacesServiceParams,
  QueryResponse,
  MerchantInfo,
  DEFAULT_GEO_CODE,
  DEFAULT_GEO_VIEWPORT,
  getMinLimit,
  useGetMerchantInfo,
} from './Tier3VenueOnboarding.helpers';
import styles from './Tier3VenueOnboarding.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  match: match;
};

const renderVenuesToOnboard = (
  classes: Styles,
  places: Place[] | [],
  searchLoading: boolean,
  viewport: GeoViewPort,
  merchantName: string,
  hasNextPage: boolean,
  handleAddMissingVenue: (newPlace: Place) => void,
  handleGetMorePlaces: Function,
) => (response: QueryResponse) => {
  if (response.loading || searchLoading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  return (
    <Container className={classes.container}>
      <div className={classes.headerContainer}>
        <Header className={classes.header} as="h3">
          Add your locations
        </Header>
        <p className={classes.headerText}>
          We found the following {merchantName} locations around your address.
          Let&apos;s make sure we&apos;ve got it all looking good.
        </p>
        <p className={classes.headerText}>
          For online-only businesses, please include your business address.
        </p>
      </div>
      <Grid>
        <GridRow>
          <GridColumn width={8}>
            <VenuesList
              places={places as Place[]}
              addMissingPlace={handleAddMissingVenue}
              getMore={handleGetMorePlaces}
              hasNextPage={hasNextPage}
            />
          </GridColumn>
          <GridColumn className={classes.map} width={8}>
            <PlacesMap places={places as Place[]} viewport={viewport} />
          </GridColumn>
        </GridRow>
      </Grid>
    </Container>
  );
};

const Tier3Onboarding: FunctionComponent<Props> = ({ classes, match }) => {
  const [places, setPlaces] = useState<Place[] | []>([]);
  const [params, setParams] = useState<PlacesServiceParams>(
    {} as PlacesServiceParams,
  );
  const [merchantName, setMerchantName] = useState<string>('');
  const [viewport, setViewport] = useState<GeoViewPort>(DEFAULT_GEO_VIEWPORT);
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const { merchantAcctDetails }: MerchantInfo = useGetMerchantInfo(match);

  const handleOnCompleted = (data: any): void => {
    const foundPlaces = pathOr([], ['getPlaces', 'places'], data);

    setPlaces([...places, ...foundPlaces]);
    setHasNextPage(pathOr(false, ['getPlaces', 'nextPage'], data));
  };

  const { loadPlacesServiceResults, searchLoading } = usePlacesSearch(
    GET_PLACES,
    {},
    handleOnCompleted,
  );

  useEffect(() => {
    if (isEmpty(merchantAcctDetails)) return;

    const {
      merchantUuid,
      companyName,
      latitude,
      longitude,
      category,
      subCategory,
      numberOfStores,
    } = merchantAcctDetails;
    const limit = getMinLimit(numberOfStores);
    const name = pathOr(companyName, ['params', 'merchantName'], match);

    let geocodeFilter = DEFAULT_GEO_CODE;
    let zoom = 3;

    if (latitude && longitude) {
      geocodeFilter = { latitude, longitude };
      zoom = 10;
    }
    setMerchantName(name);
    setViewport({
      ...DEFAULT_GEO_VIEWPORT,
      ...geocodeFilter,
      zoom,
    });
    setParams({
      merchantUuid,
      name,
      limit,
      geocodeFilter,
      categoriesFilter: [category, subCategory],
    });
  }, [merchantAcctDetails]);

  const handleAddMissingVenue = (newPlace: Place) => {
    setPlaces([...places, newPlace]);
  };

  const handleGetMorePlaces = () => {
    if (hasNextPage) {
      const offset = page + 1;
      const { limit } = params as any;
      setPage(offset);
      loadPlacesServiceResults({
        variables: {
          params: {
            ...params,
            offset: limit * offset,
          },
        },
      });
    }
    return false;
  };

  if (isEmpty(params)) return null;

  return (
    <Query
      enableProgressInfo
      query={GET_PLACES}
      onCompleted={handleOnCompleted}
      render={renderVenuesToOnboard(
        classes,
        places,
        searchLoading,
        viewport,
        merchantName,
        hasNextPage,
        handleAddMissingVenue,
        handleGetMorePlaces,
      )}
      variables={{ params }}
    />
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(Tier3Onboarding);
